'use client';

import Dialog from '@/components/Dialog';
import Image from '@/components/Image';
import { useTranslations } from '@/i18n/use-translations';
import { useEffect, useRef, useState } from 'react';

interface ScanningDialogProps {
  isOpen: boolean;
  onCancel: () => void;
  animationTime: number;
}

export default function ScanningDialog({ isOpen, onCancel, animationTime }: ScanningDialogProps) {
  const t = useTranslations('lens-process');

  // 扫描进度相关状态
  const [scanProgress, setScanProgress] = useState(0);
  const [showCancelButton, setShowCancelButton] = useState(false);
  const showCancelButtonRef = useRef(false);
  const intervalRef = useRef<NodeJS.Timeout | null>(null);

  // 处理扫描进度
  // 设置目标进度为99%
  const target = 99;
  // 动画总时长（秒）
  const totalTimeInSeconds = animationTime; // 可以根据实际需求调整
  const totalTimeInMilliseconds = totalTimeInSeconds * 1000;
  const intervalTime = 10; // 每次更新间隔（毫秒）

  const incrementPerStep = target / (totalTimeInMilliseconds / intervalTime);
  useEffect(() => {
    if (isOpen) {
      setScanProgress(0);
      setShowCancelButton(false);
      showCancelButtonRef.current = false;

      intervalRef.current = setInterval(() => {
        setScanProgress((prev) => {
          const newProgress = prev + incrementPerStep;
          // 当进度超过60%时显示取消按钮
          if (newProgress > 60 && !showCancelButtonRef.current) {
            showCancelButtonRef.current = true;
            setShowCancelButton(true);
          }

          // 达到目标值后停止
          if (newProgress >= target) {
            if (intervalRef.current) clearInterval(intervalRef.current);
            return target;
          }

          return newProgress;
        });
      }, intervalTime);
    } else if (intervalRef.current) {
      // 清除定时器
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }

    // 组件卸载时清除定时器
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
  }, [isOpen, animationTime]);

  // 取消扫描
  const handleCancelScan = () => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
    onCancel();
  };

  return (
    <Dialog
      open={isOpen}
      title=""
      fullWidth
      footer={null}
      showCloseIcon={false}
      hideHeader
      maxWidth="sm"
      sx={{
        '& .MuiDialog-container': {
          position: 'relative',
        },
        '& .MuiPaper-root': {
          backgroundColor: 'transparent',
          boxShadow: 'none',
          position: 'static',
          maxWidth: 'none',
          width: '100vw',
        },
        '& .MuiDialogContent-root': {
          position: 'static',
        },
      }}
    >
      <div className="flex flex-col items-center justify-center w-full h-full">
        {showCancelButton && (
          <div
            className="absolute top-[32px] xl:top-[calc(50vh-190px)] right-[16px] xl:right-[calc(50vw-200px)] w-[62px] h-[25px] border border-white text-white text-[12px] rounded-[21px] flex items-center justify-center opacity-95 cursor-pointer"
            onClick={handleCancelScan}
          >
            {t('cancel')}
          </div>
        )}
        <div className="w-[155px] h-[173px] bg-primary-purple rounded-[16px] flex flex-col items-center justify-center">
          <Image src="/lens-process/scanning.png" width={106} height={106} alt={t('scanning')} />
          <div className="mt-[18px] text-white text-[18px] text-center font-bb">
            {Math.round(scanProgress)}%
          </div>
        </div>
        <div className="mt-[18px] text-white text-[16px] text-center font-bb">
          {t('beingIdentified')}
        </div>
        <p className="mt-[8px] text-[13px] leading-[20px] text-center text-white">
          {t('aLittleBitOfTimeIsNeededToMakeSureTheResultIsAccurate')}
        </p>
      </div>
    </Dialog>
  );
}
